/**
 * promotionsSlice — Redux Toolkit slice for admin automatic promotion management.
 *
 * State shape
 * -----------
 * list:        PromotionListItemDTO[]        — current page of promotions
 * pagination:  PromotionPaginationMeta | null
 * editor:
 *   promotion: PromotionDTO | null           — promotion open in the form
 * loading:     Record<PromotionsLoadingKey, boolean>
 * error:       Record<PromotionsLoadingKey, string | null>
 *
 * Thunks (list-side — CON-PROMO-005)
 * ------
 * - fetchPromotions(params?)         — GET /api/v1/admin/promotions
 * - togglePromotionStatus({ id, isActive }) — PUT /api/v1/admin/promotions/:id (optimistic)
 * - reorderPromotions(priorities)    — PUT /api/v1/admin/promotions/reorder (optimistic)
 *
 * Thunks (form-side — CON-PROMO-006)
 * ------
 * - fetchPromotion(id)               — GET /api/v1/admin/promotions/:id
 * - createPromotion(payload)         — POST /api/v1/admin/promotions
 * - updatePromotion({ id, ...payload }) — PUT /api/v1/admin/promotions/:id
 *
 * CON-PROMO-005 / CON-PROMO-006.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { isApiError } from '@dfq/shared/api/_envelope';
import { promotionsAdminApiClient } from '@dfq/shared/api/admin/promotions';
import type {
  CreatePromotionPayload,
  FetchPromotionsParams,
  PromotionDTO,
  PromotionListItemDTO,
  PromotionPaginationMeta,
  PromotionPriorityItem,
  UpdatePromotionPayload,
} from '@dfq/shared/types/promotions';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type PromotionsLoadingKey =
  | 'fetchPromotions'
  | 'fetchPromotion'
  | 'createPromotion'
  | 'updatePromotion'
  | 'togglePromotionStatus'
  | 'reorderPromotions';

export interface PromotionsState {
  list: PromotionListItemDTO[];
  pagination: PromotionPaginationMeta | null;
  editor: {
    promotion: PromotionDTO | null;
  };
  loading: Record<PromotionsLoadingKey, boolean>;
  error: Record<PromotionsLoadingKey, string | null>;
}

const initialState: PromotionsState = {
  list: [],
  pagination: null,
  editor: {
    promotion: null,
  },
  loading: {
    fetchPromotions: false,
    fetchPromotion: false,
    createPromotion: false,
    updatePromotion: false,
    togglePromotionStatus: false,
    reorderPromotions: false,
  },
  error: {
    fetchPromotions: null,
    fetchPromotion: null,
    createPromotion: null,
    updatePromotion: null,
    togglePromotionStatus: null,
    reorderPromotions: null,
  },
};

// ---------------------------------------------------------------------------
// Thunks — list-side (CON-PROMO-005)
// ---------------------------------------------------------------------------

export const fetchPromotions = createAsyncThunk(
  'promotions/fetchPromotions',
  async (params: FetchPromotionsParams | void) => {
    return promotionsAdminApiClient.fetchPromotions(params ?? {});
  },
);

export const togglePromotionStatus = createAsyncThunk(
  'promotions/togglePromotionStatus',
  async (
    { id, isActive }: { id: number; isActive: boolean },
    { rejectWithValue },
  ) => {
    try {
      const updated = await promotionsAdminApiClient.togglePromotion(id);
      return updated;
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ id, isActive: !isActive, message: err.message });
      }
      const msg = err instanceof Error ? err.message : 'Failed to update status';
      return rejectWithValue({ id, isActive: !isActive, message: msg });
    }
  },
);

export const reorderPromotions = createAsyncThunk(
  'promotions/reorderPromotions',
  async (
    { priorities, optimisticList }: { priorities: PromotionPriorityItem[]; optimisticList: PromotionListItemDTO[] },
    { rejectWithValue },
  ) => {
    try {
      await promotionsAdminApiClient.reorderPromotions({ priorities });
      return optimisticList;
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue(err.message);
      }
      const msg = err instanceof Error ? err.message : 'Failed to reorder promotions';
      return rejectWithValue(msg);
    }
  },
);

// ---------------------------------------------------------------------------
// Thunks — form-side (CON-PROMO-006)
// ---------------------------------------------------------------------------

export const fetchPromotion = createAsyncThunk(
  'promotions/fetchPromotion',
  async (id: number) => {
    return promotionsAdminApiClient.fetchPromotion(id);
  },
);

export const createPromotion = createAsyncThunk(
  'promotions/createPromotion',
  async (payload: CreatePromotionPayload, { rejectWithValue }) => {
    try {
      return await promotionsAdminApiClient.createPromotion(payload);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ message: err.message, errors: err.errors });
      }
      throw err;
    }
  },
);

export const updatePromotion = createAsyncThunk(
  'promotions/updatePromotion',
  async (
    { id, ...payload }: { id: number } & UpdatePromotionPayload,
    { rejectWithValue },
  ) => {
    try {
      return await promotionsAdminApiClient.updatePromotion(id, payload);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ message: err.message, errors: err.errors });
      }
      throw err;
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const promotionsSlice = createSlice({
  name: 'promotions',
  initialState,
  reducers: {
    /** Optimistically flip a promotion's status before the API resolves. */
    optimisticToggle(state, action: PayloadAction<{ id: number; isActive: boolean }>) {
      const item = state.list.find((p) => p.id === action.payload.id);
      if (item) {
        // Map the boolean to the computed status enum used by the wire DTO.
        item.status = action.payload.isActive ? 'active' : 'inactive';
      }
    },
    /** Optimistically reorder the list before the API resolves. */
    optimisticReorder(state, action: PayloadAction<PromotionListItemDTO[]>) {
      state.list = action.payload;
    },
    /** Clear editor state when leaving the form. */
    clearEditor(state) {
      state.editor.promotion = null;
      state.error.fetchPromotion = null;
      state.error.createPromotion = null;
      state.error.updatePromotion = null;
    },
  },
  extraReducers: (builder) => {
    // ── fetchPromotions ──────────────────────────────────────────────────────
    builder
      .addCase(fetchPromotions.pending, (state) => {
        state.loading.fetchPromotions = true;
        state.error.fetchPromotions = null;
      })
      .addCase(fetchPromotions.fulfilled, (state, action) => {
        state.loading.fetchPromotions = false;
        state.list = action.payload.promotions;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchPromotions.rejected, (state, action) => {
        state.loading.fetchPromotions = false;
        state.error.fetchPromotions =
          action.error.message ?? 'Failed to fetch promotions';
      });

    // ── togglePromotionStatus ────────────────────────────────────────────────
    builder
      .addCase(togglePromotionStatus.pending, (state) => {
        state.loading.togglePromotionStatus = true;
        state.error.togglePromotionStatus = null;
      })
      .addCase(togglePromotionStatus.fulfilled, (state, action) => {
        state.loading.togglePromotionStatus = false;
        const idx = state.list.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) {
          state.list[idx] = action.payload as unknown as PromotionListItemDTO;
        }
      })
      .addCase(togglePromotionStatus.rejected, (state, action) => {
        state.loading.togglePromotionStatus = false;
        const payload = action.payload as { id: number; isActive: boolean; message: string } | undefined;
        if (payload) {
          // Revert the optimistic toggle on failure.
          const item = state.list.find((p) => p.id === payload.id);
          if (item) {
            item.status = payload.isActive ? 'active' : 'inactive';
          }
          state.error.togglePromotionStatus = payload.message;
        } else {
          state.error.togglePromotionStatus =
            action.error.message ?? 'Failed to toggle status';
        }
      });

    // ── reorderPromotions ────────────────────────────────────────────────────
    builder
      .addCase(reorderPromotions.pending, (state) => {
        state.loading.reorderPromotions = true;
        state.error.reorderPromotions = null;
      })
      .addCase(reorderPromotions.fulfilled, (state, action) => {
        state.loading.reorderPromotions = false;
        state.list = action.payload;
      })
      .addCase(reorderPromotions.rejected, (state, action) => {
        state.loading.reorderPromotions = false;
        state.error.reorderPromotions =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to reorder promotions';
      });

    // ── fetchPromotion ───────────────────────────────────────────────────────
    builder
      .addCase(fetchPromotion.pending, (state) => {
        state.loading.fetchPromotion = true;
        state.error.fetchPromotion = null;
        state.editor.promotion = null;
      })
      .addCase(fetchPromotion.fulfilled, (state, action) => {
        state.loading.fetchPromotion = false;
        state.editor.promotion = action.payload;
      })
      .addCase(fetchPromotion.rejected, (state, action) => {
        state.loading.fetchPromotion = false;
        state.error.fetchPromotion = action.error.message ?? 'Failed to load promotion';
      });

    // ── createPromotion ──────────────────────────────────────────────────────
    builder
      .addCase(createPromotion.pending, (state) => {
        state.loading.createPromotion = true;
        state.error.createPromotion = null;
      })
      .addCase(createPromotion.fulfilled, (state) => {
        state.loading.createPromotion = false;
      })
      .addCase(createPromotion.rejected, (state, action) => {
        state.loading.createPromotion = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error.createPromotion =
          payload?.message ?? action.error.message ?? 'Failed to create promotion';
      });

    // ── updatePromotion ──────────────────────────────────────────────────────
    builder
      .addCase(updatePromotion.pending, (state) => {
        state.loading.updatePromotion = true;
        state.error.updatePromotion = null;
      })
      .addCase(updatePromotion.fulfilled, (state, action) => {
        state.loading.updatePromotion = false;
        state.editor.promotion = action.payload;
      })
      .addCase(updatePromotion.rejected, (state, action) => {
        state.loading.updatePromotion = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error.updatePromotion =
          payload?.message ?? action.error.message ?? 'Failed to update promotion';
      });
  },
});

export const { optimisticToggle, optimisticReorder, clearEditor } = promotionsSlice.actions;
export const promotionsReducer = promotionsSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface PromotionsRootState {
  promotions: PromotionsState;
}

// List-side selectors (CON-PROMO-005)
export const selectPromotionsList = (state: PromotionsRootState): PromotionListItemDTO[] =>
  state.promotions.list;

export const selectPromotionsPagination = (
  state: PromotionsRootState,
): PromotionPaginationMeta | null => state.promotions.pagination;

export const selectPromotionsLoading = (
  state: PromotionsRootState,
): Record<PromotionsLoadingKey, boolean> => state.promotions.loading;

export const selectPromotionsError = (
  state: PromotionsRootState,
): Record<PromotionsLoadingKey, string | null> => state.promotions.error;

// Form-side selectors (CON-PROMO-006)
export const selectPromotion = (state: PromotionsRootState): PromotionDTO | null =>
  state.promotions.editor.promotion;

export const selectPromotionLoadingKey =
  (key: PromotionsLoadingKey) =>
  (state: PromotionsRootState): boolean =>
    state.promotions.loading[key];

export const selectPromotionErrorKey =
  (key: PromotionsLoadingKey) =>
  (state: PromotionsRootState): string | null =>
    state.promotions.error[key];
